import { React, useCallback, useEffect, useState, useMemo } from 'react';
import debounce from "lodash.debounce";
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

import Button from "../../../ui/Button";
import InputBox from "../../../ui/InputBox";
import Pagination from "../../../ui/Pagination";
import RadioButton from "../../../ui/RadioButton";
import NoDataFound from "../../../ui/NoDataFound";
import SkeletonLoader from "../../../ui/SkeletonLoader";
import { toaster } from "../../../ui/Toaster";
import DropDown from "../../../ui/DropDown";

import { getloggedInUserType } from "../../../../utils/util";
import { editIcon, searchIcon } from "../../../../utils/icons";

import { useSelector, useDispatch } from 'react-redux';
import { getInvitedUsersList, resetInvitedUsersList, deleteUserInvite, resetDeleteUserInvite, resendUserInvite, resetResendUserInvite, editUserInvite, resetEditUserInvite } from "../../../../store/actions/usersAction";
import { getCustomersList, resetCustomersList, getCustomerDetails, resetCustomerDetailResponse } from '../../../../store/actions/customerAction';

import "./index.scss";

const InvitedUserListModal = () =>{

    const invitedUsersListResponse = useSelector((state)=> state?.users?.invitedUsersList);
    const deleteUserInviteResponse = useSelector((state) => state?.users?.deleteUserInvite);
    const resendInviteResponse = useSelector((state) => state.users.resendInvite);
    const editInviteuserResponse = useSelector((state) => state.users.editInviteuser);

    const customersListResponse = useSelector((state) => state.customer?.customersListResponse);    
    const customerDetailResponse = useSelector((state) => state.customer?.customerDetailResponse);

    const dispatch = useDispatch();

    const { t } = useTranslation();

    const [showEditUser, setShowEdituser] = useState(false);
    const [invitedUsersList, setInvitedUsersList] = useState(null);
    const [searchValue, setSearchValue] = useState("");
    const [updatedSearchValue, setUpdatedSearchValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const [name, setName] = useState("");
    const [roleType, setRoleType] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [userType, setUserType] = useState("");
    const [selectedCustomer, setSelectedCustomer] = useState("");
    const [selectedEditInvite, setSelectedEditInvite] = useState("");
    const [editInviteLoading, setEditInviteLoading] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(10);

    const [deleteInviteLoading, setDeleteInviteLoading] = useState(false);
    const [resendInviteLoading, setResendInviteLoading] = useState(false);
    const [selectedUserInvite, setSelectedUserInvite] = useState("");
    const [selectedResendUserInvite, setSelectedResendUserInvite] = useState("");

    const [customersList, setCustomersList] = useState([]);    
    const [customerSearch, setCustomerSearch] = useState("");
    const [customerSearchLoading, setCustomerSearchLoading] = useState(false); 
    const [customerListCurrentPage, setCustomerListCurrentPage] = useState(1);
    const [customerListPageLoading, setCustomerListPageLoading] = useState(false);
    const [customerListTotalPage, setCustomerListTotalPage] = useState(0);
    

    const setCurrentPageandRecordSize = (currentPage, pageSize) => {
        setCurrentPage(currentPage);
        if(pageSize !== currentPageSize) {
            setCurrentPageSize(pageSize);
            setCurrentPage(1);
        }
    }

    const roleChange = (value) => {
        setRoleType(value)
    }

    const searchUsers = (event) => {
        setSearchValue(event.target.value);
        requestSearch(event.target.value)
        setCurrentPage(1);       
    }

    const requestSearch = useMemo(() => debounce((searchTerm) => {        
        setUpdatedSearchValue(searchTerm)
    }, 500), []);

    const fetchInvitedUsersList = useCallback(()=>{
        setIsLoading(true)
        const baseUrl = `&page=${currentPage}&page_size=${currentPageSize}`;
        const queryParams = updatedSearchValue ? `&query=${updatedSearchValue}` : "";
        const url = `${baseUrl}${queryParams}`;
        dispatch(getInvitedUsersList(url))
    },[currentPage, currentPageSize, updatedSearchValue, dispatch])

    const customerChange = (value) => {
        setSelectedCustomer(value.value);
    }

    const fetchCustomersList = useCallback(()=>{
        const baseUrl = `&page=${customerListCurrentPage}&page_size=10`;
        const queryParams = customerSearch ? `&query=${customerSearch}` : "";
        const url = `${baseUrl}${queryParams}`;
        dispatch(getCustomersList(url));
    },[dispatch, customerSearch, customerListCurrentPage])

    const fetchCustomerDetails = useCallback(()=>{
        let obj = {
            customer_id: selectedCustomer
        }
        dispatch(getCustomerDetails(obj))
    },[selectedCustomer, dispatch])

    const deleteInvite = (data) => {
        setSelectedUserInvite(data?.invite_id)
        let obj = {
            invite_id: data?.invite_id
        }
        setDeleteInviteLoading(true)
        dispatch(deleteUserInvite(obj))
    }

    const resendInvite = (data) => {
        setSelectedResendUserInvite(data?.invite_id)
        let obj = {
            invite_id: data?.invite_id
        }
        setResendInviteLoading(true)
        dispatch(resendUserInvite(obj))
    }

    const saveEditInvite = () => {
        let obj = {
            invite_id: selectedEditInvite?.invite_id,
            name: name,
            email: userEmail,
            role: roleType
        }
        if(getloggedInUserType() === "internal") {
            obj.user_type = userType;
            if(userType === "customer") {
                obj.customer_id = selectedCustomer;
            }
        }
        setEditInviteLoading(true)
        dispatch(editUserInvite(obj))
    }

    useEffect(() => {
        if (getloggedInUserType() === "internal" && showEditUser && userType === "customer") {
            fetchCustomersList();
        }
    }, [fetchCustomersList, showEditUser, userType]);

    useEffect(()=>{
        fetchInvitedUsersList()
    },[fetchInvitedUsersList])

    useEffect(()=>{
        if(invitedUsersListResponse !== null) {
            setIsLoading(false);
            setInvitedUsersList(invitedUsersListResponse)
            dispatch(resetInvitedUsersList())
        }
    },[invitedUsersListResponse, dispatch])

    useEffect(()=>{
        if(deleteUserInviteResponse !== null) {
            if(deleteUserInviteResponse?.status === "success") {
                toaster.addToast(deleteUserInviteResponse?.description, "success")
            } else {
                toaster.addToast(deleteUserInviteResponse?.description, "error")
            }
            setDeleteInviteLoading(false)
            setSelectedUserInvite("")
            fetchInvitedUsersList()
            dispatch(resetDeleteUserInvite())
        }
    },[deleteUserInviteResponse, fetchInvitedUsersList, dispatch])

    useEffect(()=>{
        if(resendInviteResponse !== null) {
            if(resendInviteResponse?.status === "success") {
                toaster.addToast(resendInviteResponse?.description, "success")
            } else {
                toaster.addToast(resendInviteResponse?.description, "error")
            }
            setResendInviteLoading(false)
            setSelectedResendUserInvite("")
            dispatch(resetResendUserInvite())
        }
    },[resendInviteResponse, dispatch])

    useEffect(() => {
        if (customersListResponse != null) {
            setCustomerSearchLoading(false)
            setCustomerListPageLoading(false)
            const list = customersListResponse.results.map((data) => ({
                id: data.customer_id,
                name: data.name,
                value: data.customer_id,
                logo: data.logo,
                label: data.name
            }));
            setCustomersList((prevList) => {
                const uniqueList = [...prevList, ...list].reduce((acc, item) => {
                    if (!acc.some(existingItem => existingItem.id === item.id)) {
                        acc.push(item);
                    }
                    return acc;
                }, []);
                return uniqueList; //Prevents duplicate additions, avoiding redundant state updates
            });
            const selectedValue = list.find(
                (option) => option.value === selectedCustomer
            );
            if(!selectedValue && selectedCustomer !== null) {
                fetchCustomerDetails()
            }
            setCustomerListTotalPage(customersListResponse?.meta?.total_pages)
            dispatch(resetCustomersList())
        }
    }, [customersListResponse, fetchCustomerDetails, selectedCustomer, dispatch]);

    useEffect(()=>{
        if(customerDetailResponse !== null) {
            let obj = {
                id: selectedCustomer,
                name: customerDetailResponse?.name,
                value: selectedCustomer,
                logo: customerDetailResponse?.logo,
                label: customerDetailResponse?.name
            }
            setSelectedCustomer(obj.value)
            let isAvailableInCustomerList = customersList.some((data) => data.id === selectedCustomer)
            if(!isAvailableInCustomerList) {
                setCustomersList([...customersList, obj]);
            }
            dispatch(resetCustomerDetailResponse())
            
        }
    },[customerDetailResponse, selectedCustomer, customersList, customersListResponse, dispatch])

    useEffect(()=>{
        if(editInviteuserResponse !== null) {
            if(editInviteuserResponse?.status === "success") {
                toaster.addToast(editInviteuserResponse?.description, "success")
            } else {
                toaster.addToast(editInviteuserResponse?.description, "error")
            }
            setEditInviteLoading(false)
            setSelectedEditInvite("")
            setShowEdituser(false)
            fetchInvitedUsersList()
            dispatch(resetEditUserInvite())
        }
    },[editInviteuserResponse, dispatch, fetchInvitedUsersList])


    return (
        <>
            <div className="userlist-container">
                {!showEditUser && <>
                    <div className="modal-header d-flex justify-content-between my-4">
                        <h2>Invited Users</h2>
                        <div className="search-container position-relative">
                            <input type="text" className="search-input border rounded" placeholder="Search User" value={searchValue} onChange={searchUsers}/>
                            <span className="search-icon position-absolute">{searchIcon()}</span>
                        </div>
                    </div>
                    <Row>
                    {
                        isLoading ?
                        <Col xl={12}>
                            <div className="organization-container mt-4 px-4 py-3 rounded h-100">
                                <SkeletonLoader type={"line"} count={4} currentPage={"users"} />
                            </div>
                        </Col>
                        :
                        <>
                            {
                                invitedUsersList?.results?.length > 0 &&
                                invitedUsersList?.results?.map((data, index)=>{
                                    return (
                                        <Col lg={6} className="my-2" key={index}>
                                            <motion.div
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.4 }}
                                            >
                                                <div className="user-detail-container border rounded p-3 h-100">
                                                    <div className="d-flex justify-content-between">
                                                        <div className="user-details">
                                                            <p className="mb-2 d-flex text-capitalize"><span className="label d-inline-block">Name:</span> <span className="value d-inline-block">{data?.name}</span></p>
                                                            <p className="mb-2 d-flex"><span className="label d-inline-block">Email:</span> <span className="value d-inline-block">{data?.email}</span></p>
                                                            <p className="mb-2 d-flex text-capitalize"><span className="label d-inline-block">Role:</span> <span className="value d-inline-block">{data?.role}</span></p>
                                                        </div>
                                                        <div className="edit-icon">
                                                            <span className="cursor-pointer" onClick={()=>{
                                                                setSelectedEditInvite(data)
                                                                setName(data?.name)
                                                                setUserEmail(data?.email)
                                                                setRoleType(data?.role)
                                                                if(getloggedInUserType() === "internal") {
                                                                    setUserType(data?.user_type)
                                                                    setSelectedCustomer(data?.customer_detail?.customer_id)
                                                                }                                                            
                                                                setShowEdituser(true)}
                                                            }>{editIcon()}</span>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex gap-3 mt-3 justify-content-center invited-users-actions">
                                                        {(selectedResendUserInvite === "" || selectedResendUserInvite !== data?.invite_id) && <button className="user-action-button" onClick={()=>{resendInvite(data)}}>Resend</button>}
                                                        {selectedResendUserInvite === data?.invite_id && resendInviteLoading && <button className="user-action-button" disabled>Loading...</button>}
                                                        {(selectedUserInvite === "" || selectedUserInvite !== data?.invite_id) && <button className="user-action-button delete-btn" onClick={()=>{deleteInvite(data)}}>Delete</button>}
                                                        {selectedUserInvite === data?.invite_id && deleteInviteLoading && <button className="user-action-button delete-btn" disabled>Loading...</button>}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        </Col>
                                    )
                                })
                            }
                        </>
                    }
                    </Row>                    
                    {invitedUsersList != null && !isLoading && invitedUsersList?.meta?.total_results !== 0 && <Pagination
                        className="pagination-bar"
                        currentPage={currentPage}
                        pageSize={currentPageSize}
                        totalCount={invitedUsersList?.meta?.total_results}
                        onPageChange={setCurrentPageandRecordSize}
                    />}
                    {
                        !isLoading && invitedUsersList?.meta?.total_results === 0 && 
                        <NoDataFound noDataText={"No data Found"}/>
                    }
                </>}
                {
                    showEditUser && 
                    <>
                        <div className="modal-header my-4">
                            <h2>Edit Invited Users</h2>                            
                        </div>
                        <div className="add-organization-container mt-3">
                            <div className="add-organization-step-one mt-3">
                                <div className="input-container mt-4">
                                    <InputBox placeholder={'Enter Name'} inputType={'text'} inputLabel={'What is the Name of the User?'} inputChange={(val)=>{setName(val)}} inputValue={name} />
                                </div>
                                <div className="input-container">
                                    <InputBox placeholder={'Enter Email ID'} inputType={'text'} inputLabel={'What is the Email ID?'} inputChange={(val)=>{setUserEmail(val)}} inputValue={userEmail} />
                                </div>
                                <div className="organization-type">
                                    <p>{t("users.inviteUserModal.selectRole")}</p>
                                    <div className="radio-btn-group d-flex mt-3">
                                        <RadioButton radioId={'administrator'} radioLabel={'Administrator'} radioValue={'administrator'} onChange={roleChange}  isChecked={roleType === 'administrator'}/>
                                        <RadioButton radioId={'user'} radioLabel={'User'} radioValue={'user'} onChange={roleChange} isChecked={roleType === 'user'}/>
                                        <RadioButton radioId={'readOnly'} radioLabel={'Read Only'} radioValue={'read_only'} onChange={roleChange} isChecked={roleType === 'read_only'}/>
                                    </div>
                                </div>
                                {getloggedInUserType() === "internal" && <>
                                    <div className="organization-type mt-4">
                                        <p>User type</p>
                                        <div className="radio-btn-group d-flex mt-3">
                                            <RadioButton radioId={'internal'} radioLabel={'Internal'} radioValue={'internal'} onChange={(value)=>{setUserType(value)}}  isChecked={userType === 'internal'}/>
                                            <RadioButton radioId={'customer'} radioLabel={'Customer'} radioValue={'customer'} onChange={(value)=>{setUserType(value)}} isChecked={userType === 'customer'}/>
                                        </div>
                                    </div>
                                    <div className="organization-type mt-4">
                                        <p className="mb-2">Select Customer</p>
                                            <DropDown
                                                options={customersList}
                                                placeHolder={"Select Customer"}
                                                onChange={customerChange}
                                                isSearchable= {true}
                                                isMulti={false}
                                                defaultSelectedValue={selectedCustomer}
                                                disabled={userType === 'internal'}
                                                dropDownType = {'scanInfo'}
                                                imageType={"customer"}
                                                searchType={"server"}
                                                updateSearchValue={(data)=>{
                                                    setCustomersList([])
                                                    setCustomerSearchLoading(true)
                                                    setCustomerSearch(data)
                                                    setCustomerListCurrentPage((prevPage) => (prevPage !== 1 ? 1 : prevPage)); 
                                                }}
                                                isSearchLoading={customerSearchLoading}
                                                onScrollEnd = {()=>{
                                                    if(!customerListPageLoading && (customerListTotalPage > customerListCurrentPage)) {
                                                        setCustomerListPageLoading(true)
                                                        setCustomerListCurrentPage((prevPage) => prevPage + 1)
                                                    }
                                                }}
                                                pageLoading={customerListPageLoading}
                                            />
                                    </div>
                                </>}
                            </div>
                            <div className="form-submit-btn-group mt-4">
                                <Button buttonText={"Cancel"} buttonType="secondary" buttonClicked={()=>setShowEdituser(false)}/>
                                {!editInviteLoading && <Button buttonText={"Save"} buttonType="primary" buttonClicked={()=>{saveEditInvite()}}/>}
                                {editInviteLoading && <Button buttonText={"Loading..."} buttonType="primary" disableButton={true} />}
                            </div>
                        </div>
                    </>
                }
            </div>  
        </>
    )
}

export default InvitedUserListModal;